(function() {
  'use strict';

  /*
   * IMPORTANT: SearchSpring Integration Notes
   *
   * This file currently uses Shopify Collection API as a workaround for SearchSpring CORS issues.
   *
   * To properly integrate SearchSpring (like your other pages do):
   *
   * Option 1: Add build step for this file
   * - Import @searchspring/snap-client: import { Client } from '@searchspring/snap-client';
   * - Use client properly: const client = new Client({ siteId: "u8im22" });
   * - Add to webpack/vite build process
   *
   * Option 2: Load SearchSpring SDK via CDN
   * - Add script tag: <script src="https://cdn.searchspring.net/search/v3/js/searchspring.catalog.js"></script>
   * - Use window.SearchSpring API
   *
   * Option 3: Use existing SearchSpring Snap bundle
   * - Leverage the already-loaded ss-collection-snap.js
   * - Access via window.searchspring object
   *
   * Current workaround maintains all features (wishlist, badges, etc.) while avoiding CORS.
   */

  // Function to fetch Shopify product data by ID
  // ECSR-1004: the heart on a Searchspring card gets the same treatment as the
  // theme-rendered ones — copy from the locale files (window.wishlistStrings,
  // injected by snippets/wishlist-strings.liquid) and an accessible name that
  // names the product instead of a generic "Add to wishlist" repeated down the
  // grid. No English fallback: README §8 forbids hardcoded copy here, so a
  // missing string simply yields an empty attribute value.
  //
  // The lookup itself is not reimplemented here: assets/wishlistSetup.js owns it
  // and publishes it as window.wishlistString. That script is deferred in
  // layout/theme.liquid's <head>, this one is deferred in a body section, and
  // deferred scripts run in document order — so it is always defined by the time
  // this file runs, let alone by the time a card is rendered. The typeof guard
  // only covers wishlistSetup.js failing to load at all, and degrades to the
  // same empty string.
  const wishlistCardString = (key, product) => (
    typeof window.wishlistString === 'function' ? window.wishlistString(key, product) : ''
  );

  const wishlistCardLabel = (productTitle) => (
    (productTitle && wishlistCardString('addProduct', productTitle)) || wishlistCardString('add')
  );

  // The disabled heart (no Shopify variant data for this card) is named too, in
  // the shopper's language rather than in English.
  const wishlistCardUnavailableLabel = (productTitle) => (
    (productTitle && wishlistCardString('unavailableProduct', productTitle)) || wishlistCardString('unavailableItem')
  );

  // Product fields are interpolated into attributes and into markup in a
  // template literal, so quotes and angle brackets in a title, alt text or
  // vendor name have to be neutralised.
  const escapeHtmlValue = (value) => String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // An empty aria-label is ignored by assistive technology, so emitting one
  // would leave the heart with no accessible name at all — worse than the
  // English string it replaced. Emit the attribute only when there is copy.
  const wishlistAttribute = (name, value) => (value ? `${name}="${escapeHtmlValue(value)}"` : '');

  const fetchShopifyProductById = async (productId) => {
    // Check cache first
    if (shopifyProductCache[productId]) {
      return shopifyProductCache[productId];
    }

    try {
      const response = await fetch('https://uk-jonathanadler-com.myshopify.com/api/2024-01/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': '419a7f5ba37ae5fbed3ba534f2e42eee'
        },
        body: JSON.stringify({
          query: `
            query getProduct($id: ID!) {
              product(id: $id) {
                id
                handle
                title
                vendor
                variants(first: 1) {
                  edges {
                    node {
                      id
                    }
                  }
                }
              }
            }
          `,
          variables: {
            id: `gid://shopify/Product/${productId}`
          }
        })
      });

      const data = await response.json();
      const product = data.data?.product;

      // Cache the result
      if (product) {
        shopifyProductCache[productId] = product;
      }

      return product;
    } catch (error) {
      console.error('Error fetching Shopify product:', error);
      return null;
    }
  };

  // Cache for Shopify products to avoid redundant API calls
  const shopifyProductCache = {};

  // SearchSpring API implementation with CORS workaround
  const getSearchspringProducts = async (collectionHandle, productsToShow = 12) => {
    try {
      console.log('🔍 [searchspring] Fetching products for collection:', collectionHandle);

      // IMPORTANT: Direct SearchSpring API calls are blocked by CORS
      // We need to use a proxy or SDK approach

      // Option 1: Check if SearchSpring client is available globally
      if (window.SearchSpringClient || window.searchspring?.client) {
        console.log('✅ Using existing SearchSpring client');
        const client = window.SearchSpringClient || window.searchspring.client;
        // Use the client to fetch products
        // This would require knowing the exact client API
      }

      // Option 2: Use Shopify Collection API as fallback
      // This maintains functionality while SearchSpring integration is being fixed
      console.log('⚠️ SearchSpring API blocked by CORS, using Shopify Collection API fallback');

      const response = await fetch(`/collections/${collectionHandle}/products.json?limit=${productsToShow}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch collection products: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ Fetched products from Shopify:', data.products?.length || 0);

      // Transform Shopify products to match SearchSpring format expected by renderProductCard
      if (data.products && data.products.length > 0) {
        return data.products.map(product => {
          // Map Shopify product to SearchSpring-like format
          const firstVariant = product.variants && product.variants[0];
          const price = firstVariant ? parseFloat(firstVariant.price) : 0;
          const comparePrice = firstVariant && firstVariant.compare_at_price ?
                                parseFloat(firstVariant.compare_at_price) : null;

          return {
            // Core fields expected by renderProductCard
            uid: product.id.toString(), // Shopify product ID
            id: product.id.toString(),
            ss_id: product.id.toString(),
            name: product.title,
            title: product.title,
            handle: product.handle,
            url: `/products/${product.handle}`,
            link: `/products/${product.handle}`,

            // Pricing
            price: price,
            ss_price: price,
            msrp: comparePrice,
            compare_at_price: comparePrice,
            ss_msrp: comparePrice,

            // Images - Extract src from image objects
            image: product.images && product.images[0] ? product.images[0].src : '',
            imageUrl: product.images && product.images[0] ? product.images[0].src : '',
            image_url: product.images && product.images[0] ? product.images[0].src : '',
            thumbnail: product.images && product.images[0] ? product.images[0].src : '',
            images: product.images ? product.images.map(img => img.src || img) : [],

            // Vendor
            brand: product.vendor,
            vendor: product.vendor,

            // Variants
            variant_id: firstVariant ? firstVariant.id : null,
            variants: product.variants,

            // Tags and collections
            tags: product.tags ? product.tags.join(',') : '',
            collections: product.collections || []
          };
        });
      }

      return [];

    } catch (error) {
      console.error('❌ Error fetching products:', error);

      // If all else fails, return empty array to prevent breaking
      return [];
    }
  };

  const initSearchspringCollection = async () => {
    const sections = document.querySelectorAll('.searchspring-collection-section');

    for (const section of sections) {
      const container = section.querySelector('[id^="searchspring-collection-"]');
      if (!container) continue;

      const sectionId = container.dataset.id;
      const productsToShow = parseInt(container.dataset.productsToShow) || 12;
      const columnsDesktop = parseInt(container.dataset.columnsDesktop) || 3;
      const columnsMobile = parseInt(container.dataset.columnsMobile) || 2;
      const enableDesktopSlider = container.dataset.enableDesktopSlider === 'true';
      const enableAutoplay = container.dataset.enableAutoplay === 'true';
      const autoplayDelay = parseInt(container.dataset.autoplayDelay) || 5000;
      const pauseOnHover = container.dataset.pauseOnHover === 'true';
      const enableLoop = container.dataset.enableLoop === 'true';
      const spaceBetween = parseInt(container.dataset.spaceBetween) || 30;
      const imageRatio = container.dataset.imageRatio || 'adapt';
      const imageShape = container.dataset.imageShape || 'default';
      const showSecondaryImage = container.dataset.showSecondaryImage === 'true';
      const showVendor = container.dataset.showVendor === 'true';
      const showRating = container.dataset.showRating === 'false';
      const quickAdd = container.dataset.quickAdd || 'none';
      const collectionSource = container.dataset.collectionSource || 'product_collection';
      const manualCollection = container.dataset.manualCollection;
      const productCollections = container.dataset.productCollections;

      const productsContainer = container.querySelector('.searchspring-products-container');
      if (!productsContainer) continue;

      // Determine which collection to use
      let collectionHandle = null;
      if (collectionSource === 'manual' && manualCollection) {
        collectionHandle = manualCollection;
      } else if (productCollections) {
        // Use the first collection of the current product
        const collections = productCollections.split(',');
        if (collections.length > 0) {
          collectionHandle = collections[0];
        }
      }

      if (!collectionHandle) {
        productsContainer.innerHTML = '<div class="searchspring-collection-error">No collection found for this product.</div>';
        continue;
      }

      try {
        // Fetch products from Searchspring
        const products = await getSearchspringProducts(collectionHandle, productsToShow);

        if (products && products.length > 0) {
          // Render the products with full card structure
          await renderProducts(productsContainer, products, {
            sectionId,
            columnsDesktop,
            columnsMobile,
            enableDesktopSlider,
            enableAutoplay,
            autoplayDelay,
            pauseOnHover,
            enableLoop,
            spaceBetween,
            imageRatio,
            imageShape,
            showSecondaryImage,
            showVendor,
            showRating,
            quickAdd
          });
        } else {
          productsContainer.innerHTML = '<div class="searchspring-collection-error">No products found in this collection.</div>';
        }
      } catch (error) {
        console.error('Error fetching Searchspring products:', error);
        productsContainer.innerHTML = '<div class="searchspring-collection-error">Error loading products. Please try again later.</div>';
      }
    }
  };

  async function renderProducts(container, products, options) {
    const {
      sectionId,
      columnsDesktop,
      columnsMobile,
      enableDesktopSlider,
      enableAutoplay,
      autoplayDelay,
      pauseOnHover,
      enableLoop,
      spaceBetween,
      imageRatio,
      imageShape,
      showSecondaryImage,
      showVendor,
      showRating,
      quickAdd
    } = options;

    const showMobileSlider = window.innerWidth < 750;
    const showDesktopSlider = enableDesktopSlider;

    let html = '';

    if (showDesktopSlider) {
      // Desktop slider structure with scrollbar
      html = `
        <div class="slider-component-desktop">
          <div class="swiper searchspring-collection-swiper-${sectionId}">
            <ul
              id="Slider-searchspring-${sectionId}"
              data-id="${sectionId}"
              class="swiper-wrapper product-grid contains-card contains-card--product contains-card--standard"
              role="list"
              aria-label="Product slider"
            >
      `;

      // Render all product cards asynchronously
      const productCards = await Promise.all(
        products.map((product, index) => renderProductCard(product, options, sectionId, index))
      );

      productCards.forEach((card, index) => {
        html += `
          <li id="Slide-searchspring-${sectionId}-${index + 1}" class="swiper-slide">
            ${card}
          </li>
        `;
      });

      html += `
            </ul>
          </div>
          <div class="swiper-scrollbar searchspring-collection-scrollbar-${sectionId}"></div>
          <div class="slider-buttons desktop-only">
            <button type="button" class="slider-button slider-button--prev-desktop" aria-label="Previous">
              <span class="svg-wrapper">
                <svg viewBox="0 0 20 20" class="icon icon-caret">
                  <path d="M14 7l-5 5-5-5" stroke="currentColor" fill="none" stroke-width="2"/>
                </svg>
              </span>
            </button>
            <button type="button" class="slider-button slider-button--next-desktop" aria-label="Next">
              <span class="svg-wrapper">
                <svg viewBox="0 0 20 20" class="icon icon-caret">
                  <path d="M14 7l-5 5-5-5" stroke="currentColor" fill="none" stroke-width="2"/>
                </svg>
              </span>
            </button>
          </div>
        </div>
      `;
    } else {
      // Grid structure
      html = `
        <ul
          id="Slider-searchspring-${sectionId}"
          data-id="${sectionId}"
          class="tw-flex tw-flex-row tw-p-0 tw-gap-[${spaceBetween}px] tw-list-none product-grid contains-card contains-card--product contains-card--standard grid--${columnsDesktop}-col-desktop grid--${columnsMobile}-col-tablet-down"
          role="list"
        >
      `;

      // Render all product cards asynchronously
      const productCards = await Promise.all(
        products.map((product, index) => renderProductCard(product, options, sectionId, index))
      );

      productCards.forEach((card, index) => {
        html += `
          <li id="Slide-searchspring-${sectionId}-${index + 1}" class="grid__item">
            ${card}
          </li>
        `;
      });

      html += '</ul>';
    }

    container.innerHTML = html;

    // Initialize Swiper if desktop slider is enabled
    if (showDesktopSlider) {
      setTimeout(() => {
        initializeSwiper(sectionId, options);
      }, 100);
    }

    // Initialize wishlist buttons
    initializeWishlistButtons(container);
  }

  async function renderProductCard(product, options, sectionId, index) {
    const {
      imageRatio,
      imageShape,
      showSecondaryImage,
      showVendor,
      showRating
    } = options;

    // Extract product data from Searchspring response
    // Handle various possible field names from Searchspring
    const productUrl = product.url || product.link || `/products/${product.handle || product.uid || product.id}`;
    const productTitle = product.name || product.title || '';
    const productVendor = product.brand || product.vendor || '';
    // Use uid field which contains the actual Shopify product ID
    const searchspringProductId = product.uid || product.id || product.ss_id || '';

    // Debug logging
    console.log('🔍 [searchspring] Processing product:', {
      searchspringProductId,
      productTitle,
      product
    });

    // Fetch actual Shopify product data using the Searchspring product ID
    const shopifyProduct = searchspringProductId ? await fetchShopifyProductById(searchspringProductId) : null;

    // Check if we successfully fetched Shopify product data
    let productId, variantId, hasValidShopifyData = false;

    if (shopifyProduct && shopifyProduct.id) {
      // We have valid Shopify data
      productId = shopifyProduct.id.replace('gid://shopify/Product/', '');
      variantId = shopifyProduct.variants?.edges?.[0]?.node?.id?.replace('gid://shopify/ProductVariant/', '');
      hasValidShopifyData = true;

      console.log('✅ [searchspring] Found Shopify product:', {
        productId,
        variantId,
        shopifyProduct
      });
    } else {
      // Fallback to Searchspring IDs (but these won't work with Swym)
      productId = searchspringProductId;
      variantId = product.variant_id || product.variants?.[0]?.id || product.ss_variant_id || productId;

      console.warn('⚠️ [searchspring] Could not fetch Shopify product data, wishlist will not work:', {
        searchspringProductId,
        reason: !searchspringProductId ? 'No product ID found' : 'API call failed',
        attemptedFetch: shopifyProduct
      });
    }

    // Price handling - Searchspring returns prices in different formats
    let price = product.price || product.ss_price || 0;
    let comparePrice = product.msrp || product.compare_at_price || product.ss_msrp || null;

    // Sometimes prices come as strings with $ sign
    if (typeof price === 'string') {
      price = parseFloat(price.replace(/[^0-9.-]+/g, ''));
    }
    if (typeof comparePrice === 'string') {
      comparePrice = parseFloat(comparePrice.replace(/[^0-9.-]+/g, ''));
    }

    const onSale = comparePrice && comparePrice > price;

    // Format prices
    const formattedPrice = formatPrice(price);
    const formattedComparePrice = comparePrice ? formatPrice(comparePrice) : null;

    // Images - handle various field names
    const primaryImage = product.image || product.imageUrl || product.image_url || product.thumbnail || '';
    const secondaryImage = product.images?.[1] || product.thumbnailImageUrl || product.secondary_image || '';
    const imageAlt = product.image_alt || productTitle;

    // Determine badges
    let showSaleBadge = false;
    let showSpecialOfferBadge = false;
    let showNewBadge = false;
    let showBestsellerBadge = false;

    // Check tags for badges
    if (product.tags) {
      const tags = Array.isArray(product.tags) ? product.tags : product.tags.split(',');
      tags.forEach(tag => {
        if (tag.includes('status:New')) showNewBadge = true;
        if (tag.includes('status:SpecialOffers')) showSpecialOfferBadge = true;
        if (tag.includes('status:Markdown')) showSaleBadge = true;
      });
    }

    // Check collections for bestseller
    if (product.collections) {
      const collections = Array.isArray(product.collections) ? product.collections : product.collections.split(',');
      collections.forEach(collection => {
        if (collection === 'bestsellers' || collection.includes('bestseller')) {
          showBestsellerBadge = true;
        }
      });
    }

    let cardHtml = `
      <div class="card-wrapper product-card-wrapper underline-links-hover">
        <div class="card card--standard card--media" style="--ratio-percent: 100%;">
          <div class="card__inner color-background-2 gradient ratio" style="--ratio-percent: 100%;">
            <div class="card__media">
              <div class="media media--transparent media--hover-effect">
                <img
                  srcset="${primaryImage}"
                  src="${primaryImage}"
                  sizes="(min-width: 1100px) 278px, (min-width: 750px) calc((100vw - 130px) / 4), calc((100vw - 50px) / 2)"
                  alt="${escapeHtmlValue(imageAlt)}"
                  class="motion-reduce"
                  loading="lazy"
                  width="278"
                  height="278"
                >
                ${showSecondaryImage && secondaryImage ? `
                  <img
                    srcset="${secondaryImage}"
                    src="${secondaryImage}"
                    sizes="(min-width: 1100px) 278px, (min-width: 750px) calc((100vw - 130px) / 4), calc((100vw - 50px) / 2)"
                    alt="${escapeHtmlValue(imageAlt)}"
                    class="motion-reduce"
                    loading="lazy"
                    width="278"
                    height="278"
                  >
                ` : ''}
              </div>
            </div>
            <div class="card__content">
              ${hasValidShopifyData ? `
                <button
                  type="button"
                  class="my-icon swym-button swym-add-to-wishlist swym-iconbtnlink swym-heart"
                  onclick="addToWishlist({ variantId: '${variantId}', productId: '${productId}', url: '${window.location.origin}${productUrl}' })"
                  ${wishlistAttribute('aria-label', wishlistCardLabel(productTitle))}
                  ${wishlistAttribute('title', wishlistCardString('add'))}
                  data-wishlist-product-title="${escapeHtmlValue(productTitle)}"
                  data-product-id="${productId}"
                  data-variant-id="${variantId}"
                >
                  <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' class='heart-outline' aria-hidden='true' focusable='false'>
                    <path d="M5.65362 6.95291C7.24367 5.3631 9.79892 5.32714 11.4327 6.84449L11.5522 6.95981C11.7483 7.14889 12.0603 7.1461 12.253 6.9536C13.8802 5.32649 16.5182 5.32595 18.1454 6.95291C19.6859 8.49338 19.7682 10.9405 18.3919 12.5773L18.2545 12.7327L18.1392 12.8521L11.8995 19.0918L5.66121 12.8535L5.51758 12.703H5.5162C5.43621 12.6152 5.36144 12.5242 5.29039 12.4316C4.03715 10.7983 4.15836 8.44816 5.65362 6.95291Z" stroke="#1F1F1F" fill="none" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                  <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' class='heart-filled' style='display: none;' aria-hidden='true' focusable='false'>
                    <path d="M5.65362 6.95291C7.24367 5.3631 9.79892 5.32714 11.4327 6.84449L11.5522 6.95981C11.7483 7.14889 12.0603 7.1461 12.253 6.9536C13.8802 5.32649 16.5182 5.32595 18.1454 6.95291C19.6859 8.49338 19.7682 10.9405 18.3919 12.5773L18.2545 12.7327L18.1392 12.8521L11.8995 19.0918L5.66121 12.8535L5.51758 12.703H5.5162C5.43621 12.6152 5.36144 12.5242 5.29039 12.4316C4.03715 10.7983 4.15836 8.44816 5.65362 6.95291Z" fill="#1F1F1F" stroke="none" />
                  </svg>
                </button>
              ` : `
                <button
                  type="button"
                  class="my-icon swym-button swym-add-to-wishlist swym-iconbtnlink swym-heart"
                  disabled
                  ${wishlistAttribute('aria-label', wishlistCardUnavailableLabel(productTitle))}
                  ${wishlistAttribute('title', wishlistCardString('unavailableItem'))}
                  style="opacity: 0.3; cursor: not-allowed;"
                >
                  <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' aria-hidden='true' focusable='false'>
                    <path d="M5.65362 6.95291C7.24367 5.3631 9.79892 5.32714 11.4327 6.84449L11.5522 6.95981C11.7483 7.14889 12.0603 7.1461 12.253 6.9536C13.8802 5.32649 16.5182 5.32595 18.1454 6.95291C19.6859 8.49338 19.7682 10.9405 18.3919 12.5773L18.2545 12.7327L18.1392 12.8521L11.8995 19.0918L5.66121 12.8535L5.51758 12.703H5.5162C5.43621 12.6152 5.36144 12.5242 5.29039 12.4316C4.03715 10.7983 4.15836 8.44816 5.65362 6.95291Z" stroke="#1F1F1F" fill="none" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </button>
              `}

              <div class="card__information">
                <h3 class="card__heading h5">
                  <a href="${productUrl}" class="full-unstyled-link">
                    ${escapeHtmlValue(productTitle)}
                  </a>
                </h3>
              </div>
              <div class="card__badge top left">
              ${(showSaleBadge || showSpecialOfferBadge || showNewBadge || showBestsellerBadge) ? `
                <div class="tw-flex tw-flex-wrap tw-gap-2 tw-absolute tw-top-4 tw-left-4 tw-z-10 tw-whitespace-nowrap">
                  ${showSaleBadge ? `
                    <span class="tw-inline-flex tw-items-center tw-px-3 tw-py-2 tw-font-semibold tw-bg-white sale-badge tw-uppercase tw-tracking-wide tw-leading-none">
                      CLEARANCE
                    </span>
                  ` : showSpecialOfferBadge ? `
                    <span class="tw-inline-flex tw-items-center tw-px-3 tw-py-2 tw-font-semibold tw-bg-white sale-badge tw-uppercase tw-tracking-wide tw-leading-none">
                      SPECIAL OFFER
                    </span>
                  ` : showNewBadge ? `
                    <span class="tw-inline-flex tw-items-center tw-px-3 tw-py-2 tw-font-semibold tw-bg-white new-badge tw-uppercase tw-tracking-wide tw-leading-none">
                      NEW
                    </span>
                  ` : showBestsellerBadge ? `
                    <span class="tw-inline-flex tw-items-center tw-px-3 tw-py-2 tw-font-semibold tw-bg-white bestseller-badge tw-uppercase tw-tracking-wide tw-leading-none">
                      BESTSELLER
                    </span>
                  ` : ''}
                </div>
              ` : ''}
              </div>
            </div>
          </div>
          <div class="card__content">
            <div class="card__information">
              <h3 class="card__heading h5">
                <a href="${productUrl}" class="full-unstyled-link">
                  ${escapeHtmlValue(productTitle)}
                </a>
              </h3>
              <div class="card-information">
                ${showVendor && productVendor ? `
                  <span class="visually-hidden">Vendor:</span>
                  <div class="caption-with-letter-spacing light">${escapeHtmlValue(productVendor)}</div>
                ` : ''}
                <span class="caption-large light"></span>
                <div class="price ${onSale ? 'price--on-sale' : ''}">
                  <div class="price__container">
                    ${onSale ? `
                      <div class="price__sale">
                        <span class="visually-hidden visually-hidden--inline">Sale price</span>
                        <span class="price-item price-item--sale price-item--last">${formattedPrice}</span>
                        <span class="visually-hidden visually-hidden--inline">Regular price</span>
                        <s class="price-item price-item--regular">${formattedComparePrice}</s>
                      </div>
                    ` : `
                      <div class="price__regular">
                        <span class="visually-hidden visually-hidden--inline">Regular price</span>
                        <span class="price-item price-item--regular">${formattedPrice}</span>
                      </div>
                    `}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;

    return cardHtml;
  }

  function formatPrice(price) {
    // Ensure price is a number (strip any pre-formatted currency symbols)
    const numPrice =
      typeof price === 'string'
        ? parseFloat(price.replace(/[^0-9.-]+/g, '')) || 0
        : parseFloat(price) || 0;

    // Don't convert to minor units if the price is already reasonable (under 10000)
    // This handles cases where price might already be in major units
    const priceValue = numPrice > 10000 ? numPrice / 100 : numPrice;

    // Format in the shop's active currency (GBP on the UK storefront),
    // injected by layout/theme.liquid and kept in sync with the header selector.
    const currency =
      window.SHOP_CURRENCY ||
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
      'GBP';

    try {
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(priceValue);
    } catch (e) {
      return priceValue.toFixed(2);
    }
  }

  function initializeSwiper(sectionId, options) {
    const {
      columnsDesktop,
      columnsMobile,
      enableAutoplay,
      autoplayDelay,
      pauseOnHover,
      enableLoop,
      spaceBetween
    } = options;

    const slider = document.querySelector(`.searchspring-collection-swiper-${sectionId}`);
    if (!slider || typeof Swiper === 'undefined') return;

    const swiperConfig = {
      breakpoints: {
        0: {
          slidesPerView: 1.3,
          spaceBetween: 20,
        },
        750: {
          slidesPerView: parseFloat(columnsMobile) + 0.5,
          spaceBetween: spaceBetween,
        },
        1024: {
          slidesPerView: parseFloat(columnsDesktop) + 0.2,
          spaceBetween: spaceBetween,
        },
      },
      navigation: {
        nextEl: `.searchspring-collection-section .slider-button--next-desktop`,
        prevEl: `.searchspring-collection-section .slider-button--prev-desktop`,
      },
      scrollbar: {
        el: `.searchspring-collection-scrollbar-${sectionId}`,
        draggable: true,
        hide: false,
      },
      loop: enableLoop,
      watchOverflow: true,
      slidesPerGroup: 1,
    };

    if (enableAutoplay) {
      swiperConfig.autoplay = {
        delay: autoplayDelay,
        pauseOnMouseEnter: pauseOnHover,
        disableOnInteraction: false,
      };
    }

    new Swiper(slider, swiperConfig);
  }

  function initializeWishlistButtons(container) {
    // Add CSS for heart icon toggle
    if (!document.querySelector('#searchspring-wishlist-styles')) {
      const style = document.createElement('style');
      style.id = 'searchspring-wishlist-styles';
      style.textContent = `
        .swym-heart .heart-outline { display: block; }
        .swym-heart .heart-filled { display: none !important; }
        .swym-heart.swym-added .heart-outline { display: none !important; }
        .swym-heart.swym-added .heart-filled { display: block !important; }
      `;
      document.head.appendChild(style);
    }

    // ECSR-1003: this sweep used to be a wishlist state machine of its own. It
    // called _swat.fetchLists, picked a list, and handed that list's contents to
    // updateWishlistButtonsFromContents with NO root and NO options — so after
    // every Searchspring render (filter, sort, paging) it swept the WHOLE
    // document non-additively: the PDP heart, the cart drawer hearts and every
    // product card, against a snapshot that was not window.wishlistContents and
    // without ever asking window.wishlistCacheIsAuthoritative(). A shopper whose
    // wishlist fetch had failed, or had not landed yet, watched genuinely saved
    // hearts empty out and get re-wired to addToWishlist, so the next tap saved a
    // duplicate.
    //
    // It also picked the WRONG list: it matched on `list.listName`, but Swym's
    // field is `lname` (see ensureSingleWishlist in assets/wishlistSetup.js,
    // which reads selected.lname and creates with { lname }). The .find() was
    // therefore always undefined and it always fell through to lists[0], while
    // ensureSingleWishlist takes lists.slice().reverse()[0] — the opposite end.
    // Any shopper with two Swym lists had every heart judged against a list their
    // saves never went into.
    //
    // Both problems go away by deferring to the one cache and the one contract:
    // resyncWishlistButtons scopes the sweep to this Searchspring container and
    // passes additive: !wishlistCacheIsAuthoritative(), so it can fill a heart
    // from the cache but can only clear one when the cache is known complete.
    // No Swym round trip of our own, and no second notion of which list counts.
    // ECSR-1003: no container means no scope to sweep. Falling back to the first
    // .searchspring-collection-section would sweep one section on a page the init
    // path iterates in full, so return instead of guessing at a scope.
    if (typeof window.resyncWishlistButtons !== 'function') return;
    if (!container) return;
    window.resyncWishlistButtons(container);
  }

  // Initialize on DOM content loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSearchspringCollection);
  } else {
    initSearchspringCollection();
  }

  // Re-initialize on Shopify section reload (theme editor)
  if (Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', function(event) {
      if (event.detail.sectionId && event.target.querySelector('.searchspring-collection-section')) {
        initSearchspringCollection();
      }
    });
  }
})();